import { useState } from "react";
import { Link } from "react-router-dom";
import Navbar from "@/components/feature/Navbar";
import Footer from "@/components/feature/Footer";
import FloatingCTA from "@/components/feature/FloatingCTA";
import Breadcrumbs from "@/components/feature/Breadcrumbs";
import SEO from "@/components/feature/SEO";
import { webpageSchema, breadcrumbSchema, siteUrl } from "@/lib/schemas";
import { blogPosts, blogCategories } from "@/mocks/blogPosts";
import { Search, Clock, ArrowRight, Tag } from "lucide-react";

export default function BlogPage() {
  const [activeCategory, setActiveCategory] = useState("All");
  const [searchQuery, setSearchQuery] = useState("");
  const baseUrl = siteUrl;
  const pageUrl = `${baseUrl}/blog`;

  const schemas = [
    webpageSchema(
      "Insurance & Finance Blog | ExpertAdvise.in Knowledge Center",
      "Read expert articles on health insurance, term insurance, mutual funds, SIP, tax saving, retirement planning, and personal finance. Written by certified advisors.",
      pageUrl
    ),
    breadcrumbSchema([
      { name: "Home", url: baseUrl },
      { name: "Blog", url: pageUrl },
    ]),
  ];

  const filteredPosts = blogPosts.filter((post) => {
    const matchesCategory = activeCategory === "All" || post.category === activeCategory;
    const matchesSearch =
      post.title.toLowerCase().includes(searchQuery.toLowerCase()) ||
      post.excerpt.toLowerCase().includes(searchQuery.toLowerCase()) ||
      post.tags.some((tag) => tag.toLowerCase().includes(searchQuery.toLowerCase()));
    return matchesCategory && matchesSearch;
  });

  return (
    <div className="min-h-screen bg-slate-50">
      <SEO
        title="Insurance & Finance Blog | ExpertAdvise.in Knowledge Center"
        description="Read expert articles on health insurance, term insurance, mutual funds, SIP, tax saving, retirement planning, and personal finance. Written by certified advisors."
        keywords="insurance blog India, health insurance tips, term insurance guide, mutual funds blog, personal finance India, SIP investment tips"
        canonical={pageUrl}
        ogUrl={pageUrl}
        schemas={schemas}
      />
      <Navbar />

      <section className="pt-24 md:pt-32 pb-10 md:pb-14 bg-white border-b border-slate-100">
        <div className="max-w-7xl mx-auto px-4 md:px-6">
          <div className="text-center max-w-3xl mx-auto">
            <Breadcrumbs
              items={[
                { name: "Home", href: "/" },
                { name: "Blog" },
              ]}
              className="justify-center mb-4"
            />
            <span className="inline-block bg-primary-50 text-primary-700 text-xs font-semibold uppercase tracking-wider px-3 py-1 rounded-full mb-3">
              Knowledge Center
            </span>
            <h1 className="text-2xl md:text-4xl font-bold text-slate-900 mb-4">
              Insurance & Financial Blog
            </h1>
            <p className="text-slate-600 text-base md:text-lg leading-relaxed mb-8">
              Expert insights, guides, and tips on insurance, investments, tax planning, and personal finance. Written by certified advisors.
            </p>

            {/* Search */}
            <div className="max-w-xl mx-auto relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-400" />
              <input
                type="text"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                placeholder="Search articles, topics, or tags..."
                className="w-full pl-10 pr-4 py-3 bg-slate-50 border border-slate-200 rounded-xl text-sm text-slate-900 outline-none focus:border-primary-400 placeholder:text-slate-400"
              />
            </div>
          </div>
        </div>
      </section>

      {/* Categories */}
      <section className="py-6 bg-white border-b border-slate-100">
        <div className="max-w-7xl mx-auto px-4 md:px-6">
          <div className="flex flex-wrap gap-2 justify-center">
            {blogCategories.map((cat) => (
              <button
                key={cat}
                onClick={() => setActiveCategory(cat)}
                className={`px-4 py-2 rounded-full text-sm font-medium transition-colors whitespace-nowrap cursor-pointer ${
                  activeCategory === cat
                    ? "bg-primary-600 text-white"
                    : "bg-slate-100 text-slate-700 hover:bg-slate-200"
                }`}
              >
                {cat}
              </button>
            ))}
          </div>
        </div>
      </section>

      {/* Posts Grid */}
      <section className="py-10 md:py-14">
        <div className="max-w-7xl mx-auto px-4 md:px-6">
          {filteredPosts.length === 0 ? (
            <div className="text-center py-16">
              <p className="text-slate-500">No articles found matching your criteria.</p>
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5 md:gap-6">
              {filteredPosts.map((post) => (
                <Link
                  key={post.id}
                  to={`/blog/${post.slug}`}
                  className="group bg-white rounded-2xl border border-slate-200 hover:border-primary-200 transition-all duration-300 hover:shadow-lg overflow-hidden"
                >
                  <div className="h-44 bg-gradient-to-br from-primary-100 to-primary-200 flex items-center justify-center">
                    <span className="text-primary-300 text-6xl font-bold opacity-30">
                      {post.category.charAt(0)}
                    </span>
                  </div>
                  <div className="p-5 md:p-6">
                    <div className="flex items-center gap-2 mb-3">
                      <span className="bg-primary-50 text-primary-700 text-[10px] font-bold uppercase tracking-wider px-2 py-1 rounded-full">
                        {post.category}
                      </span>
                      <span className="flex items-center gap-1 text-xs text-slate-400">
                        <Clock className="w-3 h-3" />
                        {post.readTime}
                      </span>
                    </div>
                    <h3 className="text-base font-bold text-slate-900 mb-2 group-hover:text-primary-700 transition-colors line-clamp-2">
                      {post.title}
                    </h3>
                    <p className="text-sm text-slate-500 leading-relaxed line-clamp-2 mb-4">
                      {post.excerpt}
                    </p>
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-2">
                        <div className="w-7 h-7 rounded-full bg-primary-100 text-primary-700 flex items-center justify-center text-xs font-bold">
                          {post.author.split(" ").map((n) => n[0]).join("")}
                        </div>
                        <span className="text-xs text-slate-500">{post.author}</span>
                      </div>
                      <span className="text-xs text-slate-400">{post.date}</span>
                    </div>
                    <div className="flex items-center gap-1.5 mt-4 pt-4 border-t border-slate-100">
                      <Tag className="w-3.5 h-3.5 text-slate-400" />
                      {post.tags.slice(0, 3).map((tag) => (
                        <span key={tag} className="text-[10px] bg-slate-100 text-slate-600 px-2 py-0.5 rounded-full">
                          {tag}
                        </span>
                      ))}
                    </div>
                  </div>
                </Link>
              ))}
            </div>
          )}
        </div>
      </section>

      <section className="py-12 md:py-16 bg-primary-50">
        <div className="max-w-3xl mx-auto px-4 md:px-6 text-center">
          <h2 className="text-xl md:text-2xl font-bold text-slate-900 mb-3">
            Have a Specific Question?
          </h2>
          <p className="text-slate-600 mb-6">
            Our advisors are happy to answer your insurance and investment questions personally.
          </p>
          <Link
            to="/book-appointment?source=Blog%20Page"
            className="inline-block bg-cta-600 hover:bg-cta-700 text-white font-semibold px-6 py-3 rounded-full transition-colors whitespace-nowrap"
          >
            Book Free Consultation
          </Link>
        </div>
      </section>

      <Footer />
      <FloatingCTA />
    </div>
  );
}